import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";
import clsx from "clsx";
import { ChevronDownIcon, XIcon } from "lucide-react";

export type FilterOption<T> = { label: string; value: T };

interface Props<T> {
  options: FilterOption<T>[];
  selected?: FilterOption<T>;
  onChange: (selected: T | undefined) => void;
  renderOption: (option: FilterOption<T>) => React.ReactNode;
  defaultLabel: string;
}

export function FilterButton<T>(props: Props<T>) {
  return (
    <Listbox
      value={props.selected ? props.selected.value : undefined}
      onChange={(value) => props.onChange(value as T | undefined)}
    >
      <div className="flex items-center">
        <ListboxButton
          className={clsx(
            "relative block w-full  border py-1  border-gray-200 pr-8 pl-3 text-left text-sm/6 text-gray-500 font-semibold",
            "focus:not-data-focus:outline-none data-focus:outline-2 data-focus:-outline-offset-2 data-focus:outline-white/25",
            props.selected ? "rounded-l-lg" : "rounded-lg",
            props.selected
              ? " bg-blue-50 border-blue-200 hover:bg-blue-100"
              : "bg-white border-gray-200 hover:bg-gray-50",
          )}
        >
          {props.selected
            ? props.renderOption(props.selected)
            : props.defaultLabel}
          <ChevronDownIcon
            className="group pointer-events-none absolute top-2 right-2.5 size-4 fill-white/60"
            aria-hidden="true"
          />
        </ListboxButton>
        {props.selected ? (
          <button
            onClick={() => props.onChange(undefined)}
            className={clsx(
              "relative block rounded-r-lg bg-blue-50 hover:bg-blue-100 border border-blue-200 py-2 px-2 text-left text-sm/6 text-gray-500 font-semibold",
              "focus:not-data-focus:outline-none data-focus:outline-2 data-focus:-outline-offset-2 data-focus:outline-white/25",
            )}
          >
            <XIcon className="size-4 fill-white/60" />
          </button>
        ) : null}
      </div>
      <ListboxOptions
        anchor="bottom"
        transition
        className={clsx(
          "z-30 rounded-xl border border-gray-200 mt-0.5 bg-white p-1 focus:outline-none",
          "data-[closed]:data-[leave]:opacity-0 data-[leave]:translate-y-1 data-[leave]:transition data-[leave]:duration-100 data-[leave]:ease-in",
          "data-[enter]:transition data-[enter]:duration-150 data-[enter]:ease-out data-[closed]:data-[enter]:opacity-0 data-[closed]:data-[enter]:-translate-y-5 data-[enter]:translate-y-0 data-[enter]:opacity-100",
        )}
      >
        {props.options.map((option) => (
          <ListboxOption
            key={option.label}
            value={option.value}
            className="group flex cursor-pointer hover:bg-gray-50 items-center gap-2 rounded-lg px-1.5 py-2 select-none data-focus:bg-white/10"
          >
            {props.renderOption(option)}
          </ListboxOption>
        ))}
      </ListboxOptions>
    </Listbox>
  );
}
